import { useMemo, useState } from 'react'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid2'
import LinearProgress from '@mui/material/LinearProgress'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import CustomAvatar from '@core/components/mui/avatar'
import CustomChip from '@core/components/mui/chip'
import AppLink from '@components/link'
import DiligenceDialog from '@components/due-diligence/diligence-dialog'
import KpiCard from '@components/due-diligence/kpi-card'
import ProgressRing from '@components/due-diligence/progress-ring'

import { useTRPC } from '@/lib/trpc'
import { requireSubscription } from '@/lib/route-guards'
import { DILIGENCE_STATUS, formatDate, percentOf, pluralize } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/')({
  beforeLoad: requireSubscription,
  component: Dashboard
})

function Dashboard() {
  const trpc = useTRPC()
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data: diligences = [], isPending } = useQuery(trpc.diligences.list.queryOptions({}))

  const totals = useMemo(() => {
    const certificates = diligences.reduce((total, item) => total + item.certificateCount, 0)
    const resolved = diligences.reduce((total, item) => total + item.resolvedCount, 0)

    return {
      certificates,
      resolved,
      adverse: diligences.reduce((total, item) => total + item.adverseCount, 0),
      inProgress: diligences.filter(item => item.status === 'inProgress').length
    }
  }, [diligences])

  // The most recent one gets the big card: it is almost always the one in hand.
  const featured = diligences[0]
  const recent = diligences.slice(0, 6)

  if (isPending) {
    return (
      <Grid container spacing={6}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Skeleton variant='rounded' height={232} />
        </Grid>
        {[0, 1, 2, 3].map(index => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
            <Skeleton variant='rounded' height={108} />
          </Grid>
        ))}
        <Grid size={12}>
          <Skeleton variant='rounded' height={280} />
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12, md: 6 }}>
          {featured ? (
            <Card
              className='bs-full relative overflow-hidden'
              sx={{
                background:
                  'linear-gradient(135deg, var(--mui-palette-primary-main) 0%, var(--mui-palette-primary-dark) 100%)',
                color: 'common.white'
              }}
            >
              <CardContent className='flex flex-col justify-between gap-6 bs-full'>
                <div>
                  <div className='flex items-center gap-2 mbe-3'>
                    <CustomChip
                      round='true'
                      size='small'
                      label={DILIGENCE_STATUS[featured.status].label}
                      sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'common.white' }}
                    />
                    <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Em destaque
                    </Typography>
                  </div>
                  <Typography variant='h4' sx={{ color: 'common.white' }}>
                    {featured.projectName}
                  </Typography>
                  <div className='flex flex-wrap items-center gap-4 mbs-2'>
                    {featured.address && (
                      <Typography
                        variant='body2'
                        sx={{ color: 'rgba(255,255,255,0.75)' }}
                        className='flex items-center gap-1'
                      >
                        <i className='tabler-map-pin text-base' /> {featured.address}
                      </Typography>
                    )}
                    <Typography
                      variant='body2'
                      sx={{ color: 'rgba(255,255,255,0.75)' }}
                      className='flex items-center gap-1'
                    >
                      <i className='tabler-building text-base' /> {pluralize(featured.subjectCount, 'análise', 'análises')}
                    </Typography>
                  </div>
                </div>

                <div className='flex items-end justify-between gap-4'>
                  <div>
                    <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Progresso
                    </Typography>
                    <div className='flex items-baseline gap-2'>
                      <Typography variant='h3' sx={{ color: 'common.white' }}>
                        {percentOf(featured.resolvedCount, featured.certificateCount)}%
                      </Typography>
                      <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        {featured.resolvedCount} / {featured.certificateCount} certidões
                      </Typography>
                    </div>
                  </div>
                  <Button
                    component={AppLink}
                    href={`/diligences/${featured.id}`}
                    variant='contained'
                    color='inherit'
                    endIcon={<i className='tabler-arrow-up-right' />}
                    sx={{
                      backgroundColor: 'common.white',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'common.white' }
                    }}
                  >
                    Abrir
                  </Button>
                </div>

                <LinearProgress
                  variant='determinate'
                  value={percentOf(featured.resolvedCount, featured.certificateCount)}
                  sx={{
                    blockSize: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': { backgroundColor: 'common.white' }
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            <Card className='bs-full'>
              <CardContent className='flex flex-col items-start justify-center gap-4 bs-full'>
                <CustomAvatar skin='light' color='primary' variant='rounded' size={48}>
                  <i className='tabler-sparkles text-[26px]' />
                </CustomAvatar>
                <div>
                  <Typography variant='h5'>Nenhuma diligência ainda</Typography>
                  <Typography color='text.secondary'>
                    Crie a primeira para começar a buscar certidões e gerar o parecer.
                  </Typography>
                </div>
                <Button variant='contained' startIcon={<i className='tabler-plus' />} onClick={() => setDialogOpen(true)}>
                  Criar diligência
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid container size={{ xs: 12, md: 6 }} spacing={6}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KpiCard
              title='Diligências'
              value={diligences.length}
              caption='no workspace'
              icon='tabler-folders'
              color='primary'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KpiCard
              title='Em andamento'
              value={totals.inProgress}
              caption='ativas agora'
              icon='tabler-clock'
              color='info'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KpiCard
              title='Certidões analisadas'
              value={totals.resolved}
              suffix={`/ ${totals.certificates}`}
              caption={`${percentOf(totals.resolved, totals.certificates)}% concluído`}
              icon='tabler-checks'
              color='success'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KpiCard
              title='Ocorrências'
              value={totals.adverse}
              caption={totals.adverse > 0 ? 'requerem atenção' : 'nada bloqueante'}
              icon='tabler-alert-triangle'
              color={totals.adverse > 0 ? 'error' : 'success'}
            />
          </Grid>
        </Grid>

        <Grid size={12}>
          <div className='flex items-center justify-between gap-4 mbe-4'>
            <Typography variant='h5'>Diligências recentes</Typography>
            <div className='flex items-center gap-2'>
              <Button component={Link} to='/diligences' variant='tonal' color='secondary' size='small'>
                Ver todas
              </Button>
              <Button
                variant='contained'
                size='small'
                startIcon={<i className='tabler-plus' />}
                onClick={() => setDialogOpen(true)}
              >
                Nova diligência
              </Button>
            </div>
          </div>

          <Grid container spacing={6}>
            {recent.map(diligence => {
              const status = DILIGENCE_STATUS[diligence.status]

              return (
                <Grid key={diligence.id} size={{ xs: 12, md: 6, lg: 4 }}>
                  <Card
                    component={AppLink}
                    href={`/diligences/${diligence.id}`}
                    className='block bs-full no-underline transition-shadow hover:shadow-lg'
                  >
                    <CardContent className='flex flex-col gap-4'>
                      <div className='flex items-start justify-between gap-2'>
                        <div className='min-is-0'>
                          <div className='flex items-center gap-2 mbe-1'>
                            <CustomChip round='true' size='small' variant='tonal' color={status.color} label={status.label} />
                            {diligence.adverseCount > 0 && (
                              <CustomChip
                                round='true'
                                size='small'
                                variant='tonal'
                                color='error'
                                label={String(diligence.adverseCount)}
                                icon={<i className='tabler-alert-triangle' />}
                              />
                            )}
                          </div>
                          <Typography variant='h6' className='truncate'>
                            {diligence.projectName}
                          </Typography>
                          {diligence.address && (
                            <Typography variant='body2' color='text.disabled' className='truncate'>
                              {diligence.address}
                            </Typography>
                          )}
                        </div>
                        <ProgressRing value={diligence.resolvedCount} total={diligence.certificateCount} size={44} />
                      </div>

                      <div className='flex items-center justify-between gap-2'>
                        <Typography variant='caption' color='text.disabled'>
                          {pluralize(diligence.subjectCount, 'análise', 'análises')} · {formatDate(diligence.createdAt)}
                        </Typography>
                        <Typography variant='caption' className='font-medium' color='text.secondary'>
                          {diligence.resolvedCount}/{diligence.certificateCount} certidões
                        </Typography>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Grid>
      </Grid>

      <DiligenceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  )
}
